// Libraries
import React from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ProjectModel} from '@supermove/models';

// App
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import OrganizationStaffDropdown from '@shared/modules/Organization/components/OrganizationStaffDropdown';
import ProjectAssignedStaffForm, {
  ProjectAssignedStaffFormType,
} from '@shared/modules/Project/forms/ProjectAssignedStaffForm';
import useUpdateProjectAssignedStaffMutation from '@shared/modules/Project/hooks/useUpdateProjectAssignedStaffMutation';

const EditProjectAssignedStaffDrawerFields = ({
  form,
  field,
  project,
}: {
  form: ProjectAssignedStaffFormType;
  field: string;
  project: ProjectModel;
}) => {
  const {organization} = project;
  const {
    isEnabledAdditionalSalespersons,
    isEnabledProjectCoordinatorRequired,
    isEnabledProjectManagersField,
  } = organization.features;

  return (
    <React.Fragment>
      <OrganizationStaffDropdown
        index={0}
        organization={organization}
        form={form}
        field={`${field}.bookedById`}
        label={'Salesperson'}
        placeholder={'Select salesperson'}
        inputStyle={{flex: 1}}
        isSalesperson
        isResponsive
      />
      <Space height={16} />
      {isEnabledAdditionalSalespersons && (
        <React.Fragment>
          <OrganizationStaffDropdown
            index={1}
            organization={organization}
            form={form}
            field={`${field}.additionalSalespersonIds`}
            label={isEnabledProjectManagersField ? 'Project Managers' : 'Additional Salespersons'}
            placeholder={`Select ${
              isEnabledProjectManagersField ? 'project managers' : 'salespersons'
            }`}
            isMultiSelect
            inputStyle={{flex: 1}}
            isSalesperson
            isResponsive
          />
          <Space height={16} />
        </React.Fragment>
      )}
      <OrganizationStaffDropdown
        index={2}
        organization={organization}
        form={form}
        field={`${field}.coordinatedById`}
        label={'Coordinator'}
        placeholder={'Select coordinator'}
        inputStyle={{flex: 1}}
        isRequired={isEnabledProjectCoordinatorRequired}
        isCoordinator
        isResponsive
      />
      <Space height={16} />
    </React.Fragment>
  );
};

const EditProjectAssignedStaffDrawer = ({
  project,
  isOpen,
  handleClose,
  refetch,
}: {
  project: ProjectModel;
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
}) => {
  const projectAssignedStaffForm = ProjectAssignedStaffForm.edit(project);

  const {form, handleSubmit, submitting} = useUpdateProjectAssignedStaffMutation({
    projectAssignedStaffForm,
    onSuccess: () => {
      handleClose();
      refetch();
    },
    onError: (errors) => console.log({errors}),
  });

  return (
    <DrawerWithAction
      isOpen={isOpen}
      handleClose={handleClose}
      headerText={'Edit Staff'}
      isSubmitting={submitting}
      handleSubmit={handleSubmit}
    >
      <EditProjectAssignedStaffDrawerFields
        form={form}
        field={'projectAssignedStaffForm'}
        project={project}
      />
    </DrawerWithAction>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
EditProjectAssignedStaffDrawer.fragment = gql`
  ${OrganizationStaffDropdown.fragment}
  ${ProjectAssignedStaffForm.edit.fragment}

  fragment EditProjectAssignedStaffDrawer on Project {
    id
    organization {
      id
      features {
        isEnabledAdditionalSalespersons: isEnabled(feature: "ADDITIONAL_SALESPERSON_IDS_INPUT")
        isEnabledProjectCoordinatorRequired: isEnabled(feature: "PROJECT_COORDINATOR_REQUIRED")
        isEnabledProjectManagersField: isEnabled(feature: "PROJECT_MANAGERS_FIELD")
      }
      ...OrganizationStaffDropdown
    }
    ...ProjectAssignedStaffForm_edit
  }
`;

export default EditProjectAssignedStaffDrawer;
